import { createPortal } from 'react-dom';
import JxFormResizableBase from '../../../../JxControls/JxForms/JxFormResizableBase.js';
import JxTitleBar from '../../../../JxControls/JxTitleBar/JxTitleBar.jsx';
import jxWindowManager from '../../../../JxControls/JxWindowManager/JxWindowManager.jsx';
import outputMsgEngine from '../../Core/OutputMsgEngine.js';
import OutputMsgRow from './OutputMsgRow.jsx';
import { formatMsgLine } from '../../Types/OutputMsgTypes.js';
import { TITLE_BAR_ACTIONS } from './OutputMsgDlgTitleBarActions.jsx';

import './OutputMsgDlg.css';

/**
 * OutputMsgDlg — generic message-log window shared across features (UmlWorkspace,
 * CsCodeGen, etc). Resizable JxForm, single shared instance (see openOutputMsgDlg
 * below) — any feature's toolbar/tab can open it via that helper rather than
 * calling jxWindowManager.openWindow directly, so the dedup-check lives in one place.
 *
 * List is plain selectable text (native browser selection covers arbitrary-range
 * copy) + a per-row copy icon (OutputMsgRow) + a Copy All titlebar action.
 */
export default class OutputMsgDlg extends JxFormResizableBase {
    constructor(props) {
        super(props);
        this.name = 'OutputMsgDlg';
        this.schema = null;
        this.state = { ...this.state, catFilter: '', idFilter: '' };
    }

    componentDidMount() {
        super.componentDidMount();
        this._unsubscribe = outputMsgEngine.subscribe(() => this.forceUpdate());
    }

    componentWillUnmount() {
        this._unsubscribe?.();
        super.componentWillUnmount();
    }

    componentDidUpdate(prevProps) {
        super.componentDidUpdate(prevProps);
    }

    getDefaultGeo() {
        return { top: 160, left: 320, width: 520, height: 360 };
    }

    handleClose = () => {
        try {
            this.props.onClose?.();
        } catch (error) {
            this.logError('handleClose', error.message);
        }
    };

    handleClear = () => {
        outputMsgEngine.clear();
    };

    handleCatFilterChange = (e) => {
        this.setState({ catFilter: e.target.value });
    };

    handleIdFilterChange = (e) => {
        this.setState({ idFilter: e.target.value });
    };

    /**
     * Applies catFilter (exact match) + idFilter (substring match against the
     * formatted message text) — shared by render() and Copy All, so "copy
     * all" copies what's actually visible, not the unfiltered full cache.
     * @returns {object[]}
     */
    getFilteredMessages = () => {
        const { catFilter, idFilter } = this.state;
        return outputMsgEngine.getMessages().filter((msg) => {
            if (catFilter && msg.cat !== catFilter) return false;
            if (idFilter && !msg.text.includes(idFilter)) return false;
            return true;
        });
    };

    handleCopyAll = () => {
        const text = this.getFilteredMessages().map(formatMsgLine).join('\n');
        navigator.clipboard.writeText(text);
    };

    render() {
        if (!this.props.open) return null;
        const allMessages = outputMsgEngine.getMessages();
        const cats = [...new Set(allMessages.map((m) => m.cat))];
        const messages = this.getFilteredMessages();

        return createPortal(
            <div {...this.panelProps} style={this.panelStyle} className={`jx-panel omd-panel ${this.panelActiveClass}`}>
                <JxTitleBar
                    id={`${this.name}_titlebar`}
                    title="Output"
                    variant="toolwindow"
                    isDraggable={true}
                    dragHandleProps={this.dragHandleProps}
                    actions={TITLE_BAR_ACTIONS(this.handleCopyAll, this.handleClear, this.handleClose)}
                />
                <div className="omd-filter-row">
                    <select className="omd-filter-cat" value={this.state.catFilter} onChange={this.handleCatFilterChange}>
                        <option value="">All cats</option>
                        {cats.map((cat) => (
                            <option key={cat} value={cat}>
                                {cat}
                            </option>
                        ))}
                    </select>
                    <input
                        className="omd-filter-id"
                        type="text"
                        placeholder="Filter by id..."
                        value={this.state.idFilter}
                        onChange={this.handleIdFilterChange}
                    />
                </div>
                <div className="omd-list">
                    {messages.length === 0 && <div className="omd-empty">No messages</div>}
                    {messages.map((msg) => (
                        <OutputMsgRow key={msg.id} msg={msg} />
                    ))}
                </div>
                {this.renderResizeHandles()}
            </div>,
            document.body,
        );
    }
}

/**
 * Opens the single shared OutputMsgDlg instance — no-op if already open.
 * owner: 'global' so it stays open regardless of which tab is active.
 */
export function openOutputMsgDlg() {
    const existing = jxWindowManager.getAllWindows().find((w) => w.name === 'OutputMsgDlg');
    if (existing) return;
    jxWindowManager.openWindow('OutputMsgDlg', 'global');
}

jxWindowManager.registerComponent('OutputMsgDlg', OutputMsgDlg);
